import type { CheerioAPI } from "cheerio";
import type { Chapter, ChapterRef, NovelMetadata } from "../../data-model/novel.js";
import type { ChapterIndex, Url } from "../../data-model/primitives.js";
import { unsafeBrandAdapterId } from "../../data-model/primitives.js";
import { ParseError } from "../../core/errors.js";
import { normalizeText, wordCount } from "../../utils/html.js";
import { refsFromAnchors } from "../shared/anchors.js";
import { parseNovelHeader, buildNovelMetadata } from "../shared/header.js";
import { stripLeadingTitle, assertContentNonEmpty } from "../shared/content.js";
import { TRUYENFULL_SELECTORS as S } from "./selectors.js";

export function parseNovelPage($: CheerioAPI, sourceUrl: Url): NovelMetadata {
  const header = parseNovelHeader($, S.metadata, sourceUrl);

  let status: NovelMetadata["status"] = "unknown";
  if ($(S.metadata.statusFull).length > 0) status = "completed";
  else if ($(S.metadata.statusOngoing).length > 0) status = "ongoing";

  return buildNovelMetadata(header, {
    sourceUrl,
    sourceSite: unsafeBrandAdapterId("truyenfull"),
    status,
  });
}

export interface TocPageResult {
  readonly refs: ChapterRef[];
  readonly isLastPage: boolean;
  readonly totalPages?: number;
  // Largest page number reachable via numeric pagination anchors. Useful for
  // 2-3 page novels where the "Cuối" link is suppressed by the site so
  // `totalPages` is undefined.
  readonly maxLinkedPage?: number;
}

export function parseTocPage(
  $: CheerioAPI,
  startIndex: ChapterIndex,
  baseUrl: Url,
): TocPageResult {
  const refs = refsFromAnchors($, { selector: S.toc.listItem, startIndex, baseUrl });

  let totalPages: number | undefined;
  let maxLinkedPage = 0;
  $(S.toc.paginationLinks).each((_, el) => {
    const text = $(el).text().replace(/\s+/g, " ").trim();
    const href = $(el).attr("href") ?? "";
    const m = /\/trang-(\d+)\//.exec(href);
    const linkedPage = m && m[1] ? Number.parseInt(m[1], 10) : 0;
    if (linkedPage > maxLinkedPage) maxLinkedPage = linkedPage;
    if (text.startsWith("Cuối") && linkedPage > 0) {
      totalPages = linkedPage;
    }
  });

  const activeText = $(S.toc.paginationActive).first().text().replace(/\s+/g, " ").trim();
  const activePageMatch = /^(\d+)/.exec(activeText);
  const activePage =
    activePageMatch && activePageMatch[1] ? Number.parseInt(activePageMatch[1], 10) : undefined;

  // Don't infer last-page from a hardcoded items-per-page count — TruyenFull
  // has changed page sizes before and a transient short response would silently
  // truncate the novel. Two structural signals:
  // 1. "Cuối" link present + active >= totalPages (works on multi-page novels)
  // 2. Active page exists and no pagination anchor points beyond it (works on
  //    small novels where "Cuối" is absent — page 3 of 3 only links back to 2).
  const isLastPage =
    (totalPages !== undefined && activePage !== undefined && activePage >= totalPages) ||
    (activePage !== undefined && maxLinkedPage > 0 && maxLinkedPage < activePage) ||
    (activePage === 1 && maxLinkedPage === 0 && totalPages === undefined);

  const result: TocPageResult = { refs, isLastPage };
  return {
    ...result,
    ...(totalPages !== undefined ? { totalPages } : {}),
    ...(maxLinkedPage > 0 ? { maxLinkedPage } : {}),
  };
}

export function parseChapterPage($: CheerioAPI, ref: ChapterRef): Chapter {
  const body = $(S.chapter.body);
  if (body.length === 0) {
    throw new ParseError("Missing #chapter-c on chapter page", { url: ref.url });
  }
  // Dedup uses ref.title: truyenfull renders the chapter title OUTSIDE #chapter-c,
  // so there is no in-page title element to read. LATENT RISK (unverified) — if a
  // title block is ever injected INSIDE #chapter-c with text differing from
  // ref.title, the first paragraph would duplicate it. See
  // docs/surveys/2026-06-20-review-deferred-verification.md
  body.find(S.chapter.adStrip).remove();
  body.find("script, style").remove();
  const inner = body.html() ?? "";
  let content = normalizeText(inner);
  content = stripLeadingTitle(content, ref.title);
  assertContentNonEmpty(content, ref.url);
  return {
    ...ref,
    content,
    fetchedAt: new Date(),
    wordCount: wordCount(content),
  };
}
